// Basic
import { useState } from 'react';
// Store & actions
import useSessionStore from '@/app/store/session';
import useSubscriptionStore from '@/app/store/modules/subscription';
import useTabManager from '../hooks/useTabManager';
// Components
import Page from '@/common-utils/components/structure/page/Page';
import PageActions from '@/common-utils/components/renders/pageActions/PageActions';
import SubscriptionsTable from '@/app/modules/subscriptions/components/tables/SubscriptionsTable';
import PayluddSubscriptionsTable from '@/app/modules/payludd/subscriptions/components/tables/PayluddSubscriptionsTable';
import Entity from '@/common-utils/components/renders/entity/Entity';
import ModalMultipleBlockingPos from '@/app/modules/terminals/components/modals/multipleBlocking/ModalMultipleBlockingPos';
import ModalMutipleRewarehouse from '@/app/modules/terminals/components/modals/multipleRewarehouse/ModalMutipleRewarehouse';
import ModalMultipleActivationPos from '@/app/modules/terminals/components/modals/multipleActivation/ModalMultipleActivationPos';
import ModalImport from '@/common-utils/components/renders/dataTable/import/ModalImport';
import MultipleBlockingPosImportProps from '@/app/modules/terminals/components/import/MultipleBlockingPosImportProps';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/common-utils/components/ui/tabs';
// Common
import RouteUrls from '../enums/RouteUrls';
import ICONS from '@/common-utils/enums/icons';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import Permissions from '@/app/permissions/Permissions';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
import POS_TERMINALS_TABS from '../enums/tabs/PosTerminalsTabs';
import { useLocation, useNavigate } from 'react-router-dom';
import { extractQueriesFromUrl } from '@/common-utils/functions/restUtils';
// Lang
import { nsTerminal } from '@/app/i18n/namespaces';

/**
 * @returns {JSX} PosTerminals
 */
function PosTerminals() {

    // URL Params
    const navigate = useNavigate();
    const location = useLocation();
    const { view } = extractQueriesFromUrl(location.search);

    // Store
    const session = useSessionStore(s => s.session);
    const permissions = session.permissions;
    const storeRefetchSubscriptions = useSubscriptionStore(s => s.storeRefetchSubscriptions);

    // State
    const [showModalMultipleRewarehouse, setShowModalMultipleRewarehouse] = useState(false);
    const [showModalMultipleBlocking, setShowModalMultipleBlocking] = useState();
    const [showModalMultipleActivation, setShowModalMultipleActivation] = useState();
    const [activeTab, setActiveTab] = useState(Object.values(POS_TERMINALS_TABS).find(tab => tab.name === view)?.name ?? POS_TERMINALS_TABS.DEFAULT.name);
    const [showModalMultipleBlockingByCsv, setShowModalMultipleBlockingByCsv] = useState(false);

    // Hooks
    useTabManager({
        pathname: location.pathname,
        view: view,
        activeTab: activeTab,
        setActiveTab: setActiveTab,
        tabs: POS_TERMINALS_TABS,
        navigate: navigate
    });

    return (
        <Page>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <Page.Header
                    route={RouteUrls.SUBSCRIPTIONS}
                    routeBase={RouteUrls.SUBSCRIPTIONS}
                >
                    <TabsList>
                        <TabsTrigger tab={POS_TERMINALS_TABS.SUBSCRIPTIONS} activeTab={activeTab} permissions={permissions} >
                            <Entity entity='page.subscriptions.tabs.pos_terminals' />
                        </TabsTrigger>
                        <TabsTrigger tab={POS_TERMINALS_TABS.PAYLUDD_SUBSCRIPTIONS} activeTab={activeTab} permissions={permissions} >
                            <Entity entity='page.subscriptions.tabs.payludd_pos_terminals' />
                        </TabsTrigger>
                    </TabsList>
                </Page.Header>
                <Page.Body dataCy='page-terminals'>
                    <TabsContent tab={POS_TERMINALS_TABS.SUBSCRIPTIONS} permissions={permissions}>

                        <SubscriptionsTable
                            filtersEventName={setFiltersEventName('subscriptions')}
                            pageActions={
                                <PageActions actions={[
                                    {
                                        icon: ICONS.POS_TERMINAL_ACTIVATE,
                                        entity: 'actions.page.multiple_activation',
                                        namespace: nsTerminal,
                                        onClick: () => setShowModalMultipleActivation(true),
                                        visible: true,
                                        disabled: !hasRequiredPermissions({
                                            permissions: permissions,
                                            requiredPermissions: [
                                                Permissions.SUBSCRIPTION_ACTIVATE.name,
                                                Permissions.SUBSCRIPTION_PLAN_ADD_CLIENT.name,
                                                Permissions.SUBSCRIPTION_PLAN_LIST_CLIENT_PLANS.name
                                            ]
                                        }),
                                        tooltipForbidden: 'title.permissions.access_denied'
                                    },
                                    {
                                        icon: ICONS.IN_STOCK,
                                        entity: 'actions.page.multiple_rewarehouse',
                                        namespace: nsTerminal,
                                        onClick: () => setShowModalMultipleRewarehouse(true),
                                        visible: true,
                                        disabled: !hasRequiredPermissions({
                                            permissions: permissions,
                                            requiredPermissions: [Permissions.SUBSCRIPTION_REWAREHOUSE.name, Permissions.SUBSCRIPTION_LIST.name]
                                        }),
                                        tooltipForbidden: 'title.permissions.access_denied'
                                    },
                                    {
                                        isDropdown: true,
                                        visible: true,
                                        trigger: {
                                            icon: ICONS.BLOCK,
                                            entity: 'actions.page.multiple_block.title',
                                            namespace: nsTerminal,
                                            variant: 'critical',
                                            visible: true,
                                            disabled: !hasRequiredPermissions({
                                                permissions: permissions,
                                                requiredPermissions: [Permissions.BLACKLIST_ADD_ENTRY.name]
                                            }),
                                            tooltipForbidden: 'title.permissions.access_denied'
                                        },
                                        subActions: [
                                            {
                                                entity: 'actions.page.multiple_block.select',
                                                namespace: nsTerminal,
                                                onClick: () => setShowModalMultipleBlocking(true),
                                                visible: true,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [Permissions.BLACKLIST_ADD_ENTRY.name]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            },
                                            {
                                                entity: 'actions.page.multiple_block.upload',
                                                namespace: nsTerminal,
                                                onClick: () => setShowModalMultipleBlockingByCsv(true),
                                                visible: true,
                                                disabled: !hasRequiredPermissions({
                                                    permissions: permissions,
                                                    requiredPermissions: [Permissions.BLACKLIST_ADD_ENTRY.name]
                                                }),
                                                tooltipForbidden: 'title.permissions.access_denied'
                                            }
                                        ]
                                    }
                                ]}
                                    retractable
                                />
                            } />
                    </TabsContent>
                    <TabsContent tab={POS_TERMINALS_TABS.PAYLUDD_SUBSCRIPTIONS} permissions={permissions}>

                        <PayluddSubscriptionsTable filtersEventName={setFiltersEventName('payludd_subscriptions')} />

                    </TabsContent>
                </Page.Body>
            </Tabs>
            {showModalMultipleBlocking &&
                <ModalMultipleBlockingPos
                    isOpen={showModalMultipleBlocking}
                    setIsOpen={setShowModalMultipleBlocking}
                />
            }
            {showModalMultipleBlockingByCsv &&
                <ModalImport
                    isOpen={showModalMultipleBlockingByCsv}
                    setIsOpen={setShowModalMultipleBlockingByCsv}
                    importProps={{ ...MultipleBlockingPosImportProps, setLaunchRefetch: storeRefetchSubscriptions }}
                />
            }
            {showModalMultipleRewarehouse &&
                <ModalMutipleRewarehouse
                    isOpen={showModalMultipleRewarehouse}
                    setIsOpen={setShowModalMultipleRewarehouse}
                />
            }
            {showModalMultipleActivation &&
                <ModalMultipleActivationPos
                    isOpen={showModalMultipleActivation}
                    setIsOpen={setShowModalMultipleActivation}
                />
            }
        </Page >
    );
}

export default PosTerminals;